/**
 * `<WriteupPreview>` — in-app PDF preview for the candidate write-up export.
 *
 * Fetches the write-up PDF bytes once (authenticated, via `apiClient`), renders
 * every page as a stacked column of `<canvas>` elements using pdf.js, and
 * surfaces the fetched Blob to the parent via `onBlobReady` so the "Save a
 * copy" button can write the already-fetched bytes — never re-fetching.
 *
 * Why canvas-based rendering?
 * Rendering to `<canvas>` via pdf.js shows the write-up in-app without invoking
 * the browser's PDF plugin or any external viewer, so the preview is consistent
 * regardless of the machine's PDF-handler settings. The actual download is the
 * authenticated blob save in `saveBlobAsFile`.
 *
 * Two-phase render design:
 * Effect A fetches + parses the PDF, stores the parsed doc in a ref, then
 * sets state to { phase: "ready", pageCount }. This causes React to commit the
 * <canvas> elements to the DOM. Effect B depends on `state.phase === "ready"`
 * and runs AFTER that commit, so canvasRefs are guaranteed non-null when the
 * page-render loop executes. This is the React-correct guarantee and eliminates
 * the fragile `await Promise.resolve()` / rAF / setTimeout patterns.
 *
 * pdf.js lazy-loading pattern follows `PdfTextExtractor.ts` exactly (same
 * import paths, same ts-ignore annotations, same `PdfDocLite` narrowing).
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { apiClient } from "@/lib/apiClient";
import { Spinner } from "@/components/common/Spinner";

// ── pdf.js narrow types ──────────────────────────────────────────────────────
// We only use the surface that WriteupPreview touches — don't pull full pdfjs
// typings. Same pattern as PdfTextExtractor.ts.
type PdfViewport = {
  width: number;
  height: number;
};
type PdfRenderTask = {
  promise: Promise<void>;
};
type PdfPageLite = {
  getViewport(opts: { scale: number }): PdfViewport;
  render(opts: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport }): PdfRenderTask;
};
type PdfDocLite = {
  numPages: number;
  getPage(n: number): Promise<PdfPageLite>;
};

// ── Error message mapper ─────────────────────────────────────────────────────
function messageForStatus(status: number | undefined): string {
  if (status === 410) return "This write-up has expired. Close and request a new one.";
  if (status === 403 || status === 404) return "You don't have access to this write-up.";
  return "Couldn't load the write-up preview. Close and try again.";
}

function errorStatus(err: unknown): number | undefined {
  return (
    (err as { status?: number })?.status ??
    (err as { response?: { status?: number } })?.response?.status
  );
}

// ── Component ────────────────────────────────────────────────────────────────

interface WriteupPreviewProps {
  /** Tenant slug from the route (may be undefined before the route resolves). */
  tenantSlug: string | undefined;
  /** Export UUID — used to build the download URL. */
  exportId: string;
  /**
   * Called once with the fetched Blob when the PDF is ready to save.
   * The parent can hold this Blob and pass it to `saveBlobAsFile` without
   * a second network round-trip.
   */
  onBlobReady?: (blob: Blob) => void;
}

type PreviewState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; pageCount: number };

/**
 * Renders every page of the write-up PDF into a scrollable column of
 * `<canvas>` elements. Fetches once; surfaces the Blob via `onBlobReady`.
 */
export function WriteupPreview({
  tenantSlug,
  exportId,
  onBlobReady,
}: WriteupPreviewProps): JSX.Element {
  const [state, setState] = useState<PreviewState>({ phase: "loading" });
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  // Stable ref so the render effect can access the latest `onBlobReady`
  // without needing it as a dep (avoids re-running the fetch on every render).
  const onBlobReadyRef = useRef(onBlobReady);
  useEffect(() => {
    onBlobReadyRef.current = onBlobReady;
  });

  // Parsed pdf.js document stored in a ref so Effect B can access it without
  // triggering extra re-renders or needing it as a dependency.
  const docRef = useRef<PdfDocLite | null>(null);

  // ── Effect A: fetch + parse ──────────────────────────────────────────────
  // Fetches the PDF bytes, lazy-loads pdf.js, parses the document, stores it
  // in docRef, then transitions to "ready" with the page count. Does NOT
  // render pages — that is Effect B's job, which runs after the canvas elements
  // are committed to the DOM.
  useEffect(() => {
    if (!tenantSlug) {
      setState({ phase: "error", message: "Couldn't load the write-up preview. Close and try again." });
      return;
    }

    let cancelled = false;

    async function load(): Promise<void> {
      try {
        // ── Step 1: fetch the PDF bytes (authenticated via apiClient) ──────
        const resp = await apiClient.get<Blob>(
          `/api/t/${tenantSlug}/exports/${exportId}/download`,
          { responseType: "blob" },
        );
        if (cancelled) return;

        const blob = resp.data;

        // Surface the Blob to the parent NOW, before rendering, so the "Save
        // a copy" button is available even if rendering is still in progress.
        onBlobReadyRef.current?.(blob);

        // ── Step 2: lazy-load pdf.js (same pattern as PdfTextExtractor.ts) ─
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore — type mapping for legacy build is incomplete in pdfjs v4.
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
        if (cancelled) return;

        if (pdfjs.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
          pdfjs.GlobalWorkerOptions.workerSrc = await import(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore — URL-imported asset is opaque to TS.
            "pdfjs-dist/legacy/build/pdf.worker.mjs?url"
          ).then((m: { default: string }) => m.default);
        }
        if (cancelled) return;

        // ── Step 3: parse the PDF ──────────────────────────────────────────
        // IMPORTANT: pdf.js may detach the ArrayBuffer it's given. Derive the
        // Uint8Array from a fresh arrayBuffer() call so the Blob itself is
        // never consumed — the parent still holds a valid Blob for saving.
        const arrayBuffer = await blob.arrayBuffer();
        if (cancelled) return;

        const doc = (await pdfjs
          .getDocument({ data: new Uint8Array(arrayBuffer) })
          .promise) as unknown as PdfDocLite;
        if (cancelled) return;

        // ── Step 4: store doc + transition to ready ────────────────────────
        // Store in ref so Effect B can read it without being a dependency.
        // setState triggers React to commit the <canvas> elements; Effect B
        // will fire after that commit.
        docRef.current = doc;
        setState({ phase: "ready", pageCount: doc.numPages });
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: "error",
          message: messageForStatus(errorStatus(err)),
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, exportId]);

  // ── Effect B: render pages after canvases are mounted ───────────────────
  // Runs when `state.phase` transitions to "ready". By the time this effect
  // fires, React has committed all <canvas> elements from the render below,
  // so canvasRefs.current[i] are guaranteed non-null. This is the React-
  // correct guarantee: effects run after the DOM commit for the render pass
  // that produced the current state — no fragile Promise.resolve()/rAF/
  // setTimeout needed.
  const pageCount = state.phase === "ready" ? state.pageCount : 0;
  useEffect(() => {
    if (state.phase !== "ready") return;
    const doc = docRef.current;
    if (!doc) return;

    let cancelled = false;

    async function renderPages(): Promise<void> {
      if (!doc) return;

      // Choose a scale that fits a ~720px container crisply. Typical
      // write-up page width is 595pt (A4) or 612pt (US Letter). Scale to
      // fill ~720px, then multiply by devicePixelRatio for retina sharpness.
      // Cap at 3× to avoid excessive memory on 4K+ displays.
      const CONTAINER_WIDTH_PX = 720;
      const BASE_PDF_WIDTH_PT = 612; // US Letter; adjusted per page below
      const dpr = Math.min(window.devicePixelRatio ?? 1, 3);

      for (let i = 1; i <= doc.numPages; i++) {
        if (cancelled) return;
        const page = await doc.getPage(i);
        if (cancelled) return;

        // Use scale=1 to get the natural page width, then compute target scale.
        const naturalViewport = page.getViewport({ scale: 1 });
        const pageWidth = naturalViewport.width || BASE_PDF_WIDTH_PT;
        const cssScale = CONTAINER_WIDTH_PX / pageWidth;
        const renderScale = cssScale * dpr;

        const viewport = page.getViewport({ scale: renderScale });
        const canvas = canvasRefs.current[i - 1];
        if (!canvas) continue;

        // Set backing-store dimensions for the render scale, display
        // dimensions (CSS pixels) for the container fit.
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;

        const ctx = canvas.getContext("2d");
        if (!ctx) continue; // jsdom / headless — skip render, don't throw

        await page.render({ canvasContext: ctx, viewport }).promise;
      }
    }

    void renderPages();
    return () => {
      cancelled = true;
    };
  }, [state.phase, pageCount]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (state.phase === "loading") {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-6 text-text-secondary">
        <Spinner size="md" />
        <span className="text-sm">Loading preview…</span>
      </div>
    );
  }

  if (state.phase === "error") {
    return (
      <p className="py-6 text-center text-sm text-status-rejected-fg" role="alert">
        {state.message}
      </p>
    );
  }

  // Ready — render a canvas per page.
  return (
    <div className="flex flex-col items-center gap-3 overflow-y-auto">
      {Array.from({ length: state.pageCount }, (_, idx) => (
        <canvas
          key={idx}
          ref={(el) => {
            canvasRefs.current[idx] = el;
          }}
          className="rounded border border-border-default shadow-sm"
          aria-label={`Write-up page ${idx + 1} of ${state.pageCount}`}
        />
      ))}
    </div>
  );
}
